import { TENANT_STORAGE_KEY } from "@/constants";

// The LINE in-app webview can evict localStorage while the member's Firebase
// session survives in IndexedDB. Losing the tenant id there means every API
// call goes out without its x-tenant-id header and is rejected with 403
// "Tenant not found". Mirror the id into a long-lived cookie and restore it
// on eviction. Host-only on purpose (no Domain= attribute): each club lives
// on its own subdomain and must never see another club's tenant id.
const TENANT_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const readTenantCookie = (): string | null => {
  try {
    const prefix = `${TENANT_STORAGE_KEY}=`;
    const entry = globalThis.document.cookie
      .split("; ")
      .find((row) => row.startsWith(prefix));
    const value = entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
    return value && value !== "undefined" ? value : null;
  } catch {
    return null;
  }
};

const writeTenantCookie = (tenantId: string): void => {
  try {
    globalThis.document.cookie = `${TENANT_STORAGE_KEY}=${encodeURIComponent(
      tenantId,
    )}; Max-Age=${TENANT_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax; Secure`;
  } catch {
    // no-op for non-browser environments
  }
};

export const setStoredTenantId = (tenantId: string): void => {
  try {
    globalThis.localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
  } catch {
    // localStorage unavailable — the cookie below still carries the id
  }
  writeTenantCookie(tenantId);
};

export const getStoredTenantId = (): string | null => {
  let fromStorage: string | null = null;
  try {
    fromStorage = globalThis.localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    fromStorage = null;
  }
  if (fromStorage && fromStorage !== "undefined") {
    return fromStorage;
  }

  const fromCookie = readTenantCookie();
  if (fromCookie) {
    try {
      globalThis.localStorage.setItem(TENANT_STORAGE_KEY, fromCookie);
    } catch {
      // ignore — callers still get the id for this call
    }
    return fromCookie;
  }
  return null;
};

export const removeStoredTenantId = (): void => {
  try {
    globalThis.localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch {
    // no-op
  }
  try {
    globalThis.document.cookie = `${TENANT_STORAGE_KEY}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
  } catch {
    // no-op
  }
};
